<template>
  <div class="animated fadeIn">
    <div class="container">
      <b-modal  ref="modalUnsavedChanges"
                :title="$t('Webportal.Dialog.UnsavedChanges.Title')"
                :ok-title="$t('Webportal.Dialog.UnsavedChanges.Ok')"
                :cancel-title="$t('Webportal.Dialog.UnsavedChanges.Cancel')"
                no-close-on-esc
                no-close-on-backdrop
                hide-header-close>
        {{ $t("Webportal.Dialog.UnsavedChanges.Text") }}
      </b-modal>

      <b-modal  ref="modalProcessingNote"
                :title="$t('Webportal.Actions.SurveyImprovement.ProcessingNote.Title')"
                :ok-title="$t('Webportal.Dialog.Ok')"
                ok-only>
        <span v-html="$t('Webportal.Actions.SurveyImprovement.ProcessingNote', { url: $router.resolve({name: 'Notification settings'}).href })"></span>
      </b-modal>

      <b-modal
        ref="modalSurveyImprovementCreation"
        :title="$t('Webportal.Dialog.SurveyImprovementCreation.Title')"
        :ok-title="$t('Webportal.Dialog.Ok')"
        ok-only
        @ok="onSurveyImprovementCreationOk"
      >
        <div v-html="$t('Webportal.Dialog.SurveyImprovementCreation.Text', {
          limitCheckImprovementDays: surveyInstanceData?.SurveyInstance?.SurveyImprovementCreationLimitInDays ?? '',
          limitCheckRevisionDays: surveyInstanceData?.SurveyInstance?.SurveyRevisionCreationLimitInDays ?? ''
        })"></div>
      </b-modal>

      <form @submit.prevent :novalidate="true">
        <b-card class="mb-3" v-bind:class="{ spinner: isBusy }">
          <template v-slot:header>
            <h5>
              <template v-if="isEditMode">
                {{ $t("Webportal.Actions.SurveyImprovement") }}
                <span v-if="surveyImprovement">
                  #{{ surveyImprovement.SurveyImprovementVisibleId }}
                </span>
                <small  v-if="surveyImprovement"
                        class="float-end col-form-label-sm text-muted">
                  {{ getImprovementCreatedText(surveyImprovement) }}
                </small>
              </template>
              <template v-else>
                {{ $t("Webportal.Actions.SurveyImprovement.Create") }}
              </template>
            </h5>
            <survey-improvement-header  class="pt-2"
                                        :surveyImprovement="surveyImprovement"
                                        :surveyInstance="surveyInstanceData ? surveyInstanceData.SurveyInstance : null"
                                        :statusSelection="!isReadOnly && isEditMode"
                                        :statusHistoryVisible="isEditMode"
                                        :relatedSurveyImprovementsVisible="isEditMode"
                                        @statusChanged="statusChanged" />
          </template>

          <div v-if="!surveyImprovement">
            <span v-if="!isBusy">{{ $t("Webportal.NoDataFoundPlain") }}</span>
          </div>
          <div v-if="isInitialized && surveyImprovement">

            <b-list-group flush>
              <b-list-group-item class="ps-0 pe-0">
                <div class="container-fluid">
                  <b-row>
                    <b-col class="text-start text-header-band">
                      <div>
                        {{ $t('Webportal.Dashboard.KpiOverviewText')}}&nbsp;
                      </div>
                    </b-col>
                  </b-row>
                  <survey-improvement-table :kpis="kpiTableItems">
                  </survey-improvement-table>
                </div>
              </b-list-group-item>

              <b-list-group-item  class="ps-0 pe-0"
                                  v-for="(surveyImprovementEntry, index) of surveyImprovement.Entries"
                                  :key="index">

                  <b-button block
                            squared
                            v-b-toggle="`collapse-kpi-${index}`"
                            variant="primary"
                            class="text-start w-100 px-3">
                    <span class="d-flex justify-content-between align-items-center w-100">
                      <span>{{ surveyImprovementEntry.KpiName }}&nbsp;</span>
                      <font-awesome-icon size="lg"
                          :icon="['far', surveyImprovementEntry._visible === true ? 'caret-circle-up' : 'caret-circle-down']" />
                    </span>
                  </b-button>
                  <b-collapse class="mt-2"
                              v-model="surveyImprovementEntry._visible"
                              :id="`collapse-kpi-${index}`">
                    <div class="container-fluid ">
                      <b-row>
                        <b-col lg>
                          <b-form-group label-size="sm">
                            <template v-slot:label>
                              {{ $t('Webportal.Actions.SurveyImprovement.ActionsToTake') }}
                              <span v-if="surveyInstanceData"
                                :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.ClientLocation.DefaultLocalization)}`]" />
                            </template>
                            <!-- Remarks Textarea -->
                            <b-form-textarea
                              v-model="surveyImprovementEntry.Remarks"
                              :placeholder="$t('Webportal.Actions.SurveyImprovement.ActionsToTake.Placeholder')"
                              :readonly="isReadOnly || surveyImprovementEntry._isReadOnly"
                              @input="onFieldChange(index, 'Remarks', $event)"
                              max-rows="6"
                            />
                          </b-form-group>
                        </b-col>

                        <b-col v-if="isEditMode && needsTranslation" lg>
                          <b-form-group label-size="sm">
                            <template v-slot:label>
                              {{ $t('Webportal.Translated') }}
                              <span v-if="surveyInstanceData"
                                    surveyImprovementEntry
                                    :class="['flag-icon', `flag-icon-${getRegion(surveyInstanceData.SurveyInstance.Survey.DefaultLocalization)}`]" />
                            </template>

                            <!-- RemarksTranslated Textarea -->
                            <b-form-textarea
                              v-model="surveyImprovementEntry.RemarksTranslated"
                              :placeholder="$t('Webportal.Translated.Placeholder')"
                              @input="onFieldChange(index, 'RemarksTranslated', $event)"
                              :readonly="isReadOnly"
                              max-rows="6"
                            />
                          </b-form-group>
                        </b-col>
                      </b-row>

                      <file-uploader  ref="entryFileUploader"
                                      :label="$te('Webportal.Actions.SurveyImprovement.Attachments') ? $t('Webportal.Actions.SurveyImprovement.Attachments') : null"
                                      :description="$te('Webportal.Actions.SurveyImprovement.Attachments.Description') ? $t('Webportal.Actions.SurveyImprovement.Attachments.Description') : null"
                                      :value="surveyImprovementEntry.Attachments"
                                      :upload-url="attachmentUploadUrl"
                                      :upload-url-params="{ 'survey-improvement-entry-id': surveyImprovementEntry.SurveyImprovementEntryId, }"
                                      :readonly="isReadOnly"
                                      @is-busy-changed="isBusyChanged(surveyImprovementEntry, '_isFileUploaderBusy', $event)" />

                      <survey-improvement-comments  v-if="isEditMode"
                                                    v-model="surveyImprovementEntry.Comments"
                                                    :survey-improvement-id="surveyImprovement.SurveyImprovementId"
                                                    :survey-improvement-entry-id="surveyImprovementEntry.SurveyImprovementEntryId"
                                                    :allow-create="!isReadOnly"
                                                    :allow-edit="surveyImprovement.CanEditComment"
                                                    :allow-delete="surveyImprovement.CanEditComment"
                                                    @is-busy-changed="isBusyChanged(surveyImprovementEntry, '_isCommentsBusy', $event)" />
                  </div>
                </b-collapse>
              </b-list-group-item>
            </b-list-group>
          </div>

          <template v-slot:footer>
            <div class="float-end">

              <button type="button"
                      @click="cancel"
                      class="btn btn-secondary"
                      :disabled="isBusy || isChildBusy">
                <font-awesome-icon  :icon="['far', isEditMode ? 'arrow-left' : 'ban']" />
                {{ isEditMode ? $t("Webportal.Back") : $t("Webportal.Cancel") }}
              </button>

              <button v-if="!isReadOnly"
                      type="button"
                      @click="submit"
                      class="btn btn-primary"
                      :disabled="isSubmitDisabled">
                <font-awesome-icon :icon="['far', 'dot-circle']" />
                {{ isEditMode ? $t("Webportal.Save") : $t("Webportal.Actions.SendRequest") }}
              </button>

            </div>
          </template>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import ActionService from "@/services/ActionService";
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import EventBus from "@/services/EventBus.js";
import FileUploader from "@/components/FileUploader.vue";
import SurveyImprovementComments from "./SurveyImprovementComments.vue";
import SurveyImprovementHeader from "./SurveyImprovementHeader.vue";
import SurveyImprovementMixin from "@/mixins/SurveyImprovementMixin";
import SurveyImprovementTable from './SurveyImprovementTable.vue';

import useVuelidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import { useToast } from 'bootstrap-vue-next';

export default {
  name: "SurveyImprovement",

  mixins: [ AppConfig, SurveyImprovementMixin ],

  props: {
    surveyInstanceId: null,
  },

  components: {
    FileUploader,
    SurveyImprovementComments,
    SurveyImprovementHeader,
    SurveyImprovementTable
  },

  setup () {
    const { show } = useToast();
    return { v$: useVuelidate({ $autoDirty: true }), showToast: show };
  },

  validations() {
    return {
      surveyImprovement: {
        Entries: {
          $each: helpers.forEach({
            Remarks: {
              required: helpers.withMessage(
                this.$t('Webportal.Validation.Required'),
                required
              )
            },
            // Immer validieren, aber conditional required
            RemarksTranslated: {
              required: helpers.withMessage(
                this.$t('Webportal.Validation.Required'),
                // Custom Validator: Nur required wenn needsTranslation true ist
                (value) => {
                  if (!this.needsTranslation) return true; // Nicht required
                  return required.$validator(value); // Verwende built-in required
                }
              )
            },
          })
        }
      }
    };
  },

  data() {
    return {
      isBusy: false,
      surveyImprovement: null,
      surveyInstanceData: null,
      isInitialized: false,
      kpiTableItems: [],
      kpiImprovements: [],

      // ✅ Manuelles Tracking für geänderte Felder
      dirtyEntries: new Set(), // z.B. Set(['0:Remarks', '1:RemarksTranslated'])
      originalValues: {}, // Speichere Original-Werte beim Laden
    };
  },

  computed: {
    isSubmitDisabled() {
      return !this.isFormChanged || !this.isFormValid || this.isBusy || this.isChildBusy;
    },

    isFormChanged() {
      return this.dirtyEntries.size > 0;
    },

    isFormValid() {
      // Validierung läuft noch über Vuelidate
      return !this.v$.$invalid;
    },

    isEditMode() {
      return this.$route.params.id !== undefined;
    },

    isReadOnly() {
      return !this.surveyImprovement || !this.surveyImprovement.Entries || this.surveyImprovement.IsReadOnly;
    },

    needsTranslation() {
      return this.surveyInstanceData?.SurveyInstance.Survey.DefaultLocalization !=
        this.surveyInstanceData?.SurveyInstance.ClientLocation.DefaultLocalization;
    },

    attachmentUploadUrl() {
      return this.surveyImprovement?.SurveyImprovementId
        ? process.env.BASE_URL +
            `api/action/survey-improvement/${this.surveyImprovement.SurveyImprovementId}/attachment`
        : null;
    },

    isChildBusy() {
      return (
        this.surveyImprovement?.Entries?.some(
          (e) => e._isCommentsBusy || e._isFileUploaderBusy
        )
      );
    },

    surveyImprovementForSubmit() {
      if (!this.isEditMode) {
        return this.surveyImprovement;
      } else {
        const result = _.pick(this.surveyImprovement, [
          'SurveyImprovementId', 'SurveyInstanceId', 'StatusId'
        ]);

        // ✅ Nutze dirtyEntries statt $dirty
        const changedEntriesByIndex = {};

        this.dirtyEntries.forEach(key => {
          const [idx, field] = key.split(':');
          const index = parseInt(idx, 10);

          if (!changedEntriesByIndex[index]) {
            changedEntriesByIndex[index] = [];
          }
          changedEntriesByIndex[index].push(field);
        });

        result.Entries = Object.keys(changedEntriesByIndex).map(idx => {
          const index = parseInt(idx, 10);
          const entry = this.surveyImprovement.Entries[index];
          const fields = changedEntriesByIndex[index];

          const entryObj = {};
          if (entry.SurveyImprovementEntryId) {
            entryObj.SurveyImprovementEntryId = entry.SurveyImprovementEntryId;
          }

          fields.forEach(field => {
            entryObj[field] = entry[field];
          });

          return entryObj;
        });

        return result;
      }
    },

    surveyImprovementKpis() {
      return this.surveyInstanceData?.Kpis;
    }
  },

  async created() {
    this.initializeTheme();

    if (!this.isEditMode && !this.surveyInstanceId) {
      this.$router.replace({ name: "notfound" });
    }

    if (this.isEditMode) {
      await this.getSurveyImprovement(this.$route.params.id);
      if (this.surveyImprovement) {
        await this.getSurveyInstanceData(this.surveyImprovement.SurveyInstanceId);

        this.kpiTableItems.push(...this.surveyInstanceData.Kpis);
        this.kpiTableItems.push(...this.surveyInstanceData.KpiTotal);

        // get wave text
        for (const [j, kpi] of this.surveyInstanceData.Kpis.entries()) {
          kpi._visible = true;
          // kpi.YTD.Failed !== 0
        }
        for (const [j, kpi] of this.surveyInstanceData.KpiTotal.entries()) {
          kpi._visible = true;
          kpi.KpiName = this.$t('Webportal.Dashboard.Improvements.BonusAchieved');
        }
      }
    } else {
      await this.getSurveyInstanceData(this.surveyInstanceId);

      if (this.surveyInstanceData) {
        if (!this.surveyInstanceData.SurveyInstance.CanCreateSurveyImprovement) {
          // Warte, bis das Modal gerendert wurde
          await this.$nextTick();
          this.$refs.modalSurveyImprovementCreation.show();
        } else {
          // Collect data for KPI-Overview-Table
          this.kpiTableItems.push(...this.surveyInstanceData.Kpis);
          this.kpiTableItems.push(...this.surveyInstanceData.KpiTotal);

          // get wave text
          for (const [j, kpi] of this.surveyInstanceData.Kpis.entries()) {
            kpi._visible = true;
            // kpi.YTD.Failed !== 0
          }
          for (const [j, kpi] of this.surveyInstanceData.KpiTotal.entries()) {
            kpi._visible = true;
            kpi.KpiName = this.$t('Webportal.Dashboard.Improvements.BonusAchieved');
          }

          this.surveyImprovement = {
            SurveyInstanceId: this.surveyInstanceData.SurveyInstance.SurveyInstanceId,
            ExternalSurveyInstanceId: this.surveyInstanceData.SurveyInstance.ExternalSurveyInstanceId,
            StatusId: this.SurveyImprovementStatus.New,
            Entries: [],
          };

          this.surveyImprovement.Entries.push(...this.surveyInstanceData.Kpis.filter(x => !x.HasPassed));
        }
      }
    }

    EventBus.$on("LocalizationChanged", this.localizationChanged);

    this.isInitialized = true;
  },

  beforeUnmount() {
    EventBus.$off("LocalizationChanged", this.localizationChanged);
  },

  beforeRouteLeave(to, from, next) {
    this.unsavedChanges((result) => {
      if (result !== false) next();
    });
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyInstanceData(this.surveyImprovement.SurveyInstanceId);
    },

    getRegion(localization) {
      if (!localization || !localization.includes("-"))
        return "";
      else
        return localization.split("-")[1].toLowerCase();
    },

    isBusyChanged(surveyImprovementEntry, property, value) {
      surveyImprovementEntry[property] = value;
    },

    unsavedChanges(callback) {
      if (this.isFormChanged || this.isChildBusy) {
        let dialog = this.$refs.modalUnsavedChanges;

        dialog.$off();
        dialog.$on("ok", (_) => callback());
        dialog.$on("cancel", (_) => callback(false));
        dialog.show();
      } else callback();
    },

    statusChanged(statusId) {
      let currentStatus = this.surveyImprovement.StatusId;
      this.surveyImprovement.StatusId = statusId;

      // Submit and undo local status change on error
      this.submit().catch(() => {
        this.surveyImprovement.StatusId = currentStatus;
      });
    },

    getSurveyInstanceData(surveyInstanceId) {
      this.isBusy = true;

      return ActionService.getSurveyInstanceData(surveyInstanceId)
        .then((response) => {
          this.surveyInstanceData = !_.isEmpty(response.data)
            ? response.data
            : null;
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    // ✅ Speichere Original-Werte beim Laden
    getSurveyImprovement(surveyImprovementId) {
      this.isBusy = true;

      return ActionService.getSurveyImprovement(surveyImprovementId)
        .then((response) => {
          let surveyImprovement = !_.isEmpty(response.data) ? response.data : null;

          if (!_.isEmpty(surveyImprovement?.Entries))
            surveyImprovement.Entries.forEach((entry, idx) => {
              entry._visible = true;
              entry._isReadOnly = entry.Remarks != null && entry.Remarks != '';

              // Speichere Original-Werte
              this.originalValues[`${idx}:Remarks`] = entry.Remarks;
              this.originalValues[`${idx}:RemarksTranslated`] = entry.RemarksTranslated;
            });

          this.surveyImprovement = surveyImprovement;
          this.dirtyEntries.clear(); // Reset dirty tracking

          // Reset Vuelidate (für Validierung, nicht für dirty-Tracking)
          this.$nextTick(() => {
            this.v$.$reset();
          });
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    // ✅ Track Änderungen manuell
    onFieldChange(index, fieldName, value) {
      const key = `${index}:${fieldName}`;
      const originalValue = this.originalValues[key];

      // Vergleiche mit Original-Wert
      if (value !== originalValue) {
        this.dirtyEntries.add(key);
      } else {
        this.dirtyEntries.delete(key);
      }

      console.log('Field changed:', { index, fieldName, isDirty: this.dirtyEntries.has(key) });
    },

    cancel() {
      this.$router.go(-1);
    },

    onSurveyImprovementCreationOk() {
      this.$router.go(-1);
    },

    async submit() {
      this.isBusy = true;

      try {
        let response = await ActionService.setSurveyImprovement(this.surveyImprovementForSubmit);

        this.v$.$reset();

        this.showToast({
          props: {
            title: this.$t("Webportal.SaveSuccess.Text",
              { entity: this.$t("Webportal.Actions.SurveyImprovement") }),
            variant: "success",
            autoHide: true,
            delay: 2000,
          }
        });

        if (!this.isEditMode) {
          this.$router.replace({
            name: this.$route.name,
            params: { id: response.data.surveyImprovementId },
          });

          if (this.surveyInstanceData.SurveyInstance.ShowSurveyImprovementProcessingNote)
            this.$refs.modalProcessingNote.show();

          // Reload Improvement, also because component is not reloaded on eventual navigation change
          await this.getSurveyImprovement(response.data.surveyImprovementId);

          // Upload all file attachments
          for (const fileUploader of this.$refs.entryFileUploader)
            await fileUploader.uploadFiles();

        } else {
          await this.getSurveyImprovement(response.data.surveyImprovementId);
        }
      } catch (error) {
        console.error(error);

        this.showToast({
          props: {
            title: this.$t("Webportal.SaveFailed.Text", {
              entity: this.$t("Webportal.Actions.SurveyImprovement"),
            }),
            variant: "danger",
            autoHide: true,
            delay: 10000,
          }
        });

        throw error;
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.text-header-band {
  background-color: var(--bmw-bg21);
  color: var(--bmw-2021-fg);
  padding-left: 0.325rem;
  padding-right: 0.325rem;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  margin: 0.325rem;
}

.list-group-item {
  // border: none;
  background-color: transparent;
}
</style>
